/**
 * Admin → Due Payments (Phase D)
 *
 * Lists delivered deliveries that haven't been paid yet. Admin can record a
 * payment reference for each one, marking it 'paid'.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Wallet, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function adminFetch(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) throw new Error((await res.text()) || `${res.status}`);
  return res.status === 204 ? null : res.json();
}

export default function AdminDuePayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refs, setRefs] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminFetch('/api/admin/due-payments?only_delivered=true');
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    const reference = (refs[id] || '').trim();
    if (!reference) {
      toast.error('Enter a payment reference first');
      return;
    }
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await adminFetch(`/api/admin/deliveries/${id}/mark-paid`, {
        method: 'POST',
        body: JSON.stringify({ payment_reference: reference, payment_method: 'bank_transfer' }),
      });
      toast.success('Marked as paid');
      setRefs((r) => ({ ...r, [id]: '' }));
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-due-payments-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Operations · Payments</p>
          <h1 className="font-display text-3xl text-[#05223D]">Due Payments</h1>
          <p className="text-sm text-[#465B70] mt-1">Delivered orders awaiting payment. Record a bank reference to clear each line.</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="h-10 rounded-xl" data-testid="due-payments-refresh">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
        </Button>
      </header>

      {data && data.by_customer.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-[#465B70] mb-2 font-bold">By customer</p>
            <div className="flex flex-wrap gap-2">
              {data.by_customer.map((c) => (
                <Badge key={c.user_id} variant="secondary" className="rounded-full text-xs px-3 py-1">
                  {c.customer_name} · {c.deliveries} item(s)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!data ? (
        <div className="flex items-center gap-2 text-[#465B70]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : data.rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-[#465B70] bg-white rounded-2xl border border-[#0A4D8C1A]">
          <Wallet className="h-8 w-8 mb-2" /> All payments are up to date.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#0A4D8C1A] bg-white overflow-hidden" data-testid="due-payments-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-wider text-[#465B70]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Customer</th>
                <th className="text-left px-4 py-2 font-bold">Product</th>
                <th className="text-right px-4 py-2 font-bold">Qty</th>
                <th className="text-left px-4 py-2 font-bold">Delivered</th>
                <th className="text-left px-4 py-2 font-bold">Bank reference</th>
                <th className="px-4 py-2 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-[#0A4D8C14] hover:bg-[#FDFBF7]" data-testid={`due-row-${r.id}`}>
                  <td className="px-4 py-2 font-bold text-[#05223D]">{r.customer_name}<div className="text-[10px] text-[#7A8A99] font-normal">{r.customer_phone}</div></td>
                  <td className="px-4 py-2">{r.product_name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.qty}</td>
                  <td className="px-4 py-2 text-[#465B70]">{(r.delivered_at || r.scheduled_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-2">
                    <Input
                      value={refs[r.id] || ''}
                      onChange={(e) => setRefs((cur) => ({ ...cur, [r.id]: e.target.value }))}
                      placeholder="e.g. TBC #2026-001"
                      className="h-8 rounded-lg text-xs"
                      data-testid={`due-ref-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" onClick={() => markPaid(r.id)} disabled={!!busy[r.id]} className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`mark-paid-${r.id}`}>
                      {busy[r.id] ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3 w-3" />}
                      Mark paid
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
